import { MedplumClient } from "@medplum/core";
import { Encounter } from "@medplum/fhirtypes";

import { fetchEncounterProviderName } from "@/encounters/services/encounter";
import { findEncounterParticipantByType } from "@/encounters/utils/encounter-participants";
import { Status } from "@/encounters/zod-schemas/encounters";
import { EncounterPractitionerType } from "@/fhir/enums/quilted-health";
import { getDisplayStringAsync } from "@/utils/medplum";

/**
 * Human-readable provider/signer label for an encounter: when finished, prefer the
 * signed-and-locked-by participant, then meta.author; otherwise clinical provider participant,
 * then meta.author.
 */
export async function resolveEncounterProviderDisplay(
  medplum: MedplumClient,
  encounter: Encounter,
): Promise<string> {
  const isSignedAndLocked = encounter.status === Status.Finished;
  if (isSignedAndLocked) {
    const signerParticipant = findEncounterParticipantByType(
      encounter,
      EncounterPractitionerType.SignedAndLockedBy,
    );
    const fromSigner = await getDisplayStringAsync(medplum, signerParticipant?.individual);
    const trimmedSigner = fromSigner?.trim();
    if (trimmedSigner) return trimmedSigner;
    return getDisplayStringAsync(medplum, encounter.meta?.author);
  }
  const fromParticipant = await fetchEncounterProviderName(medplum, encounter.participant);
  const trimmedParticipantName = fromParticipant?.trim();
  if (trimmedParticipantName) return trimmedParticipantName;
  return getDisplayStringAsync(medplum, encounter.meta?.author);
}
